// apps/client/src/utils/commissionSplit.ts
// Commission split computation utilities for broker commission settings.
// All monetary values in integer cents. Never use floats.

export interface AssistantSplit {
  sharePct: number;      // 0-100 — % of broker's earned share
  displayName: string;   // cached for UI without users/ lookup
}

/**
 * Credit Analyst commission allocation. PR 1A-v3 (2026-05-13): separate
 * upfront + trail percentages so admins can express "Hanah takes 10% of
 * Andrew's upfront commission but 0% of his trail." Both pools (assistant
 * + analyst) deduct from the broker's earned share, but the cap is now
 * TWO independent budgets — see updateBrokerCommissionConfig two-budget
 * cap. Assistant.sharePct applies uniformly across payment types so it
 * counts in BOTH budgets.
 *
 * Legacy read-shim (handled by every read site below): pre-PR-1A-v3
 * entries had a single `sharePct` field. When upfrontSharePct/trailSharePct
 * are absent, both fall back to sharePct so byte-identical legacy behaviour
 * is preserved until the next write rewrites them.
 */
export interface CreditAnalystSplit {
  upfrontSharePct: number;  // 0-100 — % of broker's earned UPFRONT share
  trailSharePct: number;    // 0-100 — % of broker's earned TRAIL share
  displayName: string;      // cached for UI without analyst-record lookup
  /** @deprecated PR 1A-v3 read-shim source only — never written. */
  sharePct?: number;
}

export type IntroductionType = 'broker' | 'company' | 'self_sourced';
export type PaymentLeg =
  | 'upfront'
  | 'trail'
  | 'arrears'
  | 'discharged'
  | 'clawback'
  /** Third-party referrer fee. Carries no broker share; the caller must zero
   *  it before display, exactly as it does for arrears/discharged. This util
   *  collapses every non-upfront leg to the trail rate and would otherwise
   *  price it. */
  | 'referrer_deduction';

export interface CommissionConfig {
  brokerIntroductionPct: number;      // Group 2 upfront 0-100
  companyIntroductionPct: number;     // Group 1 upfront 0-100
  /** Phase 6 (3-tier) — trail % per legacy tier. OPTIONAL so existing
   *  configs keep their byte-identical "trail = upfront pct" behavior.
   *  When defined, takes precedence on the trail leg only. */
  trailBrokerIntroductionPct?: number;   // Group 2 trail 0-100
  trailCompanyIntroductionPct?: number;  // Group 1 trail 0-100
  /** Phase 6 (3-tier) — Group 3 (self-sourced) upfront + trail %.
   *  Only applied when introductionType === 'self_sourced'. */
  upfrontSelfSourcedPct?: number;        // 0-100
  trailSelfSourcedPct?: number;          // 0-100
  defaultIntroductionType: 'company'; // Always 'company' — conservative default
  effectiveFrom: Date;
  effectiveTo: Date | null;           // null = currently active
  baseSalaryCents: number | null;     // Monthly base salary in integer cents, null = no salary
  /** Monthly revenue hurdle (2026-10-06). OPTIONAL and mutually exclusive
   *  with a base salary. Absent = no hurdle, byte-identical behaviour.
   *  Maths in utils/revenueHurdle.ts. */
  monthlyRevenueHurdleCents?: number | null;
  hurdleClawbackBucket?: 'own' | 'as_tagged' | null;
  hurdleYearEndRule?: 'carry' | 'forgive' | 'clawback' | null;
  hurdleFiscalYearStartMonth?: number | null; // 1-12, default 6
  visiblePaymentTypes?: string[];     // ['upfront', 'trail'] or subset
  assistantSplits?: Record<string, AssistantSplit>; // keyed by assistant user ID
  /** Credit Analyst recipients. Same arithmetic as assistantSplits — each
   *  analyst's share comes OUT of the broker's earned portion. Sum of
   *  (assistantSplits + creditAnalystSplits) sharePcts must be ≤ 100%. */
  creditAnalystSplits?: Record<string, CreditAnalystSplit>;
}

/** Breakdown of a single commission across company / broker / recipients. */
export interface SplitBreakdown {
  /** Broker's pre-allocation share (post-company-intro). */
  brokerShareCents: number;
  /** Per-assistant allocations summed out of brokerShareCents. */
  assistantSharesCents: Array<{ userId: string; displayName: string; cents: number }>;
  /** Per-credit-analyst allocations — same pool as assistantShares. */
  creditAnalystSharesCents: Array<{ analystId: string; displayName: string; cents: number }>;
  /** brokerShareCents − Σ assistantSharesCents − Σ creditAnalystSharesCents. */
  brokerNetCents: number;
}

/**
 * Compute the broker's share of a commission based on introduction type AND
 * the row's payment leg (upfront vs trail).
 *
 * Untagged loans (introductionType = null) default to Company Introduction
 * percentage — the conservative/lower split.
 *
 * For 'broker' / 'company' tiers: when the matching `trail*Pct` field is
 * defined, the trail leg uses it; otherwise falls back to the single
 * upfront pct (legacy byte-identical behaviour for existing configs).
 *
 * For 'self_sourced' tier (Phase 6 3-tier): uses upfrontSelfSourcedPct or
 * trailSelfSourcedPct depending on the leg. Defaults to 0 if missing.
 *
 * Returns the broker's GROSS share before any assistant allocation.
 */
export function computeBrokerShare(
  commissionCents: number,
  introductionType: IntroductionType | null,
  paymentType: PaymentLeg,
  config: CommissionConfig
): number {
  // DEFAULT TO COMPANY — untagged loans get the lower split
  const type: IntroductionType = introductionType ?? 'company';
  const isTrailLeg = paymentType !== 'upfront';

  if (type === 'self_sourced') {
    const pct = isTrailLeg
      ? (config.trailSelfSourcedPct ?? 0)
      : (config.upfrontSelfSourcedPct ?? 0);
    return Math.round((commissionCents * pct) / 100);
  }

  const upfrontPct =
    type === 'broker' ? config.brokerIntroductionPct : config.companyIntroductionPct;
  if (!isTrailLeg) {
    return Math.round((commissionCents * upfrontPct) / 100);
  }

  // Trail leg: prefer explicit trail*Pct override; fall back to upfront pct
  // for byte-identical legacy behaviour when undefined.
  const trailOverride =
    type === 'broker' ? config.trailBrokerIntroductionPct : config.trailCompanyIntroductionPct;
  const trailPct =
    trailOverride !== undefined && trailOverride !== null ? trailOverride : upfrontPct;
  return Math.round((commissionCents * trailPct) / 100);
}

/**
 * Compute the full split of a commission: broker gross share, each
 * assistant's allocation, and the broker's net after assistants.
 *
 * - Assistants allocate out of the BROKER's share (not the full commission).
 *   e.g. commission=$100, broker 60%, assistant 20% → brokerShare=$60,
 *   assistant=$12, brokerNet=$48.
 * - All arithmetic in integer cents; rounding via Math.round matches
 *   computeBrokerShare.
 * - If config.assistantSplits is absent/empty, brokerNet === brokerShare.
 * - Input sharePct values should sum to ≤ 100 (server-side validated at
 *   write time). If a client-side drift produces >100, brokerNet may go
 *   negative — caller should surface via UI warning, util does not clamp.
 */
export function computeSplitBreakdown(
  commissionCents: number,
  introductionType: IntroductionType | null,
  paymentType: PaymentLeg,
  config: CommissionConfig
): SplitBreakdown {
  const brokerShareCents = computeBrokerShare(commissionCents, introductionType, paymentType, config);
  const assistantMap = config.assistantSplits ?? {};
  const analystMap = config.creditAnalystSplits ?? {};
  // Assistant share applies uniformly across payment types — UNCHANGED.
  const assistantSharesCents = Object.entries(assistantMap).map(([userId, split]) => ({
    userId,
    displayName: split.displayName,
    cents: Math.round((brokerShareCents * split.sharePct) / 100),
  }));
  // Analyst share is per-payment-type. PR 1A-v3 read-shim: legacy single
  // `sharePct` falls into both upfront and trail slots so behaviour is
  // byte-identical for any unmigrated config.
  const isUpfront = paymentType === 'upfront';
  const creditAnalystSharesCents = Object.entries(analystMap).map(([analystId, split]) => {
    const pct = isUpfront
      ? (split.upfrontSharePct ?? split.sharePct ?? 0)
      : (split.trailSharePct ?? split.sharePct ?? 0);
    return {
      analystId,
      displayName: split.displayName,
      cents: Math.round((brokerShareCents * pct) / 100),
    };
  });
  const totalAllocated =
    assistantSharesCents.reduce((s, a) => s + a.cents, 0) +
    creditAnalystSharesCents.reduce((s, a) => s + a.cents, 0);
  return {
    brokerShareCents,
    assistantSharesCents,
    creditAnalystSharesCents,
    brokerNetCents: brokerShareCents - totalAllocated,
  };
}

/** Compute a specific credit analyst's share. Zero when not allocated.
 *  PR 1A-v3: branches on paymentType to use upfrontSharePct or trailSharePct.
 *  Legacy `sharePct` shim covers any unmigrated config. */
export function computeCreditAnalystShare(
  commissionCents: number,
  introductionType: IntroductionType | null,
  paymentType: PaymentLeg,
  config: CommissionConfig,
  analystId: string
): number {
  const split = config.creditAnalystSplits?.[analystId];
  if (!split) return 0;
  const brokerShareCents = computeBrokerShare(commissionCents, introductionType, paymentType, config);
  const pct =
    paymentType === 'upfront'
      ? (split.upfrontSharePct ?? split.sharePct ?? 0)
      : (split.trailSharePct ?? split.sharePct ?? 0);
  return Math.round((brokerShareCents * pct) / 100);
}

/**
 * Compute a specific assistant's share of a commission. Zero when the
 * assistant is not in the config's assistantSplits.
 */
export function computeAssistantShare(
  commissionCents: number,
  introductionType: IntroductionType | null,
  paymentType: PaymentLeg,
  config: CommissionConfig,
  assistantUserId: string
): number {
  const split = config.assistantSplits?.[assistantUserId];
  if (!split) return 0;
  const brokerShareCents = computeBrokerShare(commissionCents, introductionType, paymentType, config);
  return Math.round((brokerShareCents * split.sharePct) / 100);
}

/**
 * Find the active commission config for a given date.
 *
 * Point-in-time lookup: finds the config whose effectiveFrom <= date < effectiveTo.
 * effectiveTo: null means currently active (no upper bound).
 */
export function findActiveConfig(
  date: Date,
  configs: CommissionConfig[]
): CommissionConfig | undefined {
  return configs.find(c => {
    const from = c.effectiveFrom;
    const to = c.effectiveTo;
    return date >= from && (to === null || date < to);
  });
}
